<script lang="ts" setup>
import { Media } from '@/models/media.model';
import { computed, PropType } from 'vue';

const props = defineProps({
  media: { type: Object as PropType<Media>, required: true },
  showOptions: { type: Array, default: () => ['use'] },
  showInfo: { type: Boolean, default: () => false },
  disabled: { type: Boolean, default: () => false },
});

const src = computed<string>(() =>
  props.media.file ? `/file/${props.media.file.uuid}/small.webp` : '',
);

const emit = defineEmits(['use', 'edit', 'remove', 'view']);

const use = () => {
  emit('use', props.media);
};
const edit = () => {
  emit('edit', props.media);
};
const remove = () => {
  emit('remove', props.media);
};
const view = () => {
  emit('view', props.media);
};
</script>
<template>
  <v-card elevation="4" class="mb-4 media-item">
    <v-card-text>
      <div :class="{ 'image-container': true, 'showing-info': !showInfo }">
        <img :src="src" class="image" />
        <div class="media-info" v-if="showInfo">
          <h4>{{ media.description }}</h4>
          <p>Purpose: {{ media.purpose }}</p>
        </div>
        <div class="language">
          <v-tooltip
            :text="`This file will be used for ${
              media.file?.language
            } language${media.file?.language === 'all' ? 's' : ''}`"
          >
            <template v-slot:activator="{ props }">
              <v-chip
                :color="media.file?.language === 'all' ? 'primary' : 'red'"
                variant="flat"
                v-bind="props"
                >{{ media.file?.language }}</v-chip
              >
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-card-text>
    <v-card-actions v-if="showOptions.length && !disabled">
      <v-btn
        density="compact"
        variant="flat"
        style="font-size: 11px"
        @click="use"
        color="success"
        v-if="showOptions.includes('use')"
        >{{ $t('media.use') }}</v-btn
      >
      <v-btn
        density="compact"
        variant="flat"
        style="font-size: 11px"
        @click="edit"
        color="primary"
        v-if="showOptions.includes('edit')"
        >{{ $t('media.editShort') }}</v-btn
      >

      <v-btn
        density="compact"
        variant="flat"
        style="font-size: 11px"
        @click="view"
        >{{ $t('media.view') }}</v-btn
      >
      <v-btn
        density="compact"
        variant="flat"
        style="font-size: 11px"
        @click="remove"
        color="warning"
        v-if="showOptions.includes('remove')"
        >{{ $t('media.remove') }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.media-item {
  min-width: 240px;
  max-width: 240px;
  margin: 0 auto;
}
.image-container {
  position: relative;
  min-height: auto;
  max-height: auto;
}
.image-container:not(.showing-info):after {
  content: '';
  background-color: rgba(0, 0, 0, 0.4);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
.media-info {
  position: absolute;
  padding: 1em;
  top: 0;
  left: 0;
  color: white;
  z-index: 2;
}
.image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  min-width: 100px;
  min-height: 100px;
}

.language {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 100;
}
</style>
